import { useCallback, useMemo } from "react";
import { FormikProps, getIn } from "formik";
import _ from "lodash";

import { CloseIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";

import Autocomplete from "components/Autocomplete";
import Button from "components/Button";
import IconButton from "components/IconButton";
import Select from "components/Select";
import Stack from "components/Stack";
import Text from "components/Text";
import TextField from "components/TextField";
import Tooltip from "components/Tooltip";

import { MemoSignatureCustomFieldType } from "entities/Memo/constants";
import { useMemoSignatureRoleList } from "entities/Memo/sdk";
import { SectionValuesType } from "entities/MemoTemplate/components/SectionForm";

export interface IMemoTemplateSignatureSectionForm {
  formik: FormikProps<SectionValuesType>;
}

const MemoTemplateSignatureSectionForm = ({
  formik,
}: IMemoTemplateSignatureSectionForm) => {
  const { data: memoSignatureRoles } = useMemoSignatureRoleList();

  const memoSignatureRoleOptions = useMemo(
    () =>
      memoSignatureRoles?.map((role) => ({
        name: role.name,
        id: role.id,
      })) || [],
    [memoSignatureRoles]
  );

  const memoSignatureRoleHandleChange = useCallback(
    (event, option: { id?: number; name: string } | null) => {
      if (_.isNil(option)) {
        formik.setFieldValue("memo_signature_role", null);
      } else if (option.id === 0) {
        formik.setFieldValue("memo_signature_role", {
          id: null,
          name: option.name,
        });
      } else {
        formik.setFieldValue("memo_signature_role", {
          id: option.id,
          name: option.name,
        });
      }
    },
    [formik]
  );

  const onAddNewCustomFieldButtonClick = useCallback(() => {
    formik.setFieldValue("custom_fields", [
      ...(formik.values.custom_fields || []),
      { name: "", field_type: "" },
    ]);
  }, [formik]);

  const onRemoveCustomFieldButtonClick = useCallback(
    ({ index }: { index: number }) => {
      const updated = [
        ...(!_.isNil(formik.values.custom_fields)
          ? formik.values.custom_fields
          : []),
      ];
      updated.splice(index, 1);
      formik.setFieldValue("custom_fields", updated);
    },
    [formik]
  );

  const selectedRoleValue = useMemo(() => {
    if (
      _.isNil(formik.values.memo_signature_role?.id) &&
      !_.isNil(formik.values.memo_signature_role?.name)
    ) {
      return formik.values.memo_signature_role;
    }

    return (
      memoSignatureRoleOptions.find(
        (role) => role.id === formik.values.memo_signature_role?.id
      ) || null
    );
  }, [formik.values.memo_signature_role, memoSignatureRoleOptions]);

  return (
    <Stack>
      <Text variant="text3" marginBottom={0.5}>
        Existing signature role
      </Text>
      <Autocomplete
        selectOnFocus
        value={selectedRoleValue}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select from the list..."
            helperText={
              getIn(formik.touched, "memo_signature_role") &&
              !formik.values.memo_signature_role &&
              getIn(formik.errors, "memo_signature_role")
            }
            error={
              getIn(formik.touched, "memo_signature_role") &&
              !formik.values.memo_signature_role &&
              !_.isEmpty(getIn(formik.errors, "memo_signature_role"))
            }
          />
        )}
        filterOptions={(options, params) => {
          let result = [...options];

          const { inputValue } = params;
          result = result.filter((option) =>
            option.name?.toLowerCase().includes(inputValue.toLowerCase())
          );

          const inputValueExists = result.some(
            (option) => option.name?.toLowerCase() === inputValue.toLowerCase()
          );

          if (inputValue !== "" && !inputValueExists) {
            result.push({
              id: 0,
              name: inputValue,
            });
          }

          return result;
        }}
        renderOption={(params, option) => (
          <Stack
            component="li"
            direction="row"
            gap={1}
            alignItems="center"
            flexWrap="wrap"
            sx={{
              padding: 1,
              "&:hover": {
                backgroundColor: colors.blue10,
              },
            }}
            {...params}
            key={params.id}
          >
            <Text fontWeight={500} variant="text2" noWrap>
              {option.id !== 0 ? "" : "Add"} {option.name}
            </Text>
          </Stack>
        )}
        options={memoSignatureRoleOptions}
        getOptionLabel={(option) => option.name}
        slotProps={{
          listbox: {
            sx: {
              border: `1px solid ${colors.gray40}`,
              padding: theme.spacing(2),
              boxShadow: `0 4px 8px 0 ${colors.gray20}`,
            },
          },
        }}
        onChange={memoSignatureRoleHandleChange}
      />

      {!_.isNil(formik.values.memo_signature_role) && (
        <Stack gap={1}>
          <TextField
            sx={{ marginTop: 2 }}
            type="number"
            label="Signature order"
            {...formik.getFieldProps("memo_signature_field_order")}
            error={
              formik.touched.memo_signature_field_order &&
              !!formik.errors.memo_signature_field_order
            }
            helperText={
              formik.touched.memo_signature_field_order &&
              formik.errors.memo_signature_field_order
                ? formik.errors.memo_signature_field_order
                : ""
            }
          />

          {formik.values.custom_fields?.map((customField, index) => (
            <Stack key={index} gap={1} direction="row">
              <TextField
                fullWidth
                label="Custom field name"
                {...formik.getFieldProps(`custom_fields[${index}].name`)}
                error={
                  !_.isNil(getIn(formik.touched, "custom_fields")) &&
                  !_.isNil(getIn(formik.touched, "custom_fields")[index]) &&
                  getIn(formik.touched, "custom_fields")[index].name &&
                  !_.isNil(getIn(formik.errors, "custom_fields")) &&
                  !_.isNil(getIn(formik.errors, "custom_fields")[index]) &&
                  getIn(formik.errors, "custom_fields")[index].name
                }
                helperText={
                  !_.isNil(getIn(formik.touched, "custom_fields")) &&
                  !_.isNil(getIn(formik.touched, "custom_fields")[index]) &&
                  getIn(formik.touched, "custom_fields")[index].name &&
                  !_.isNil(getIn(formik.errors, "custom_fields")) &&
                  !_.isNil(getIn(formik.errors, "custom_fields")[index]) &&
                  getIn(formik.errors, "custom_fields")[index].name
                    ? getIn(formik.errors, "custom_fields")[index].name
                    : ""
                }
              />
              <Select
                fullWidth
                label="Custom field type"
                options={Object.values(MemoSignatureCustomFieldType).map(
                  (type) => ({
                    value: type,
                    label: type.charAt(0).toUpperCase() + type.slice(1),
                  })
                )}
                {...formik.getFieldProps(`custom_fields[${index}].field_type`)}
                error={
                  !_.isNil(getIn(formik.touched, "custom_fields")) &&
                  !_.isNil(getIn(formik.touched, "custom_fields")[index]) &&
                  getIn(formik.touched, "custom_fields")[index].field_type &&
                  !_.isNil(getIn(formik.errors, "custom_fields")) &&
                  !_.isNil(getIn(formik.errors, "custom_fields")[index]) &&
                  getIn(formik.errors, "custom_fields")[index].field_type
                }
                helperText={
                  !_.isNil(getIn(formik.touched, "custom_fields")) &&
                  !_.isNil(getIn(formik.touched, "custom_fields")[index]) &&
                  getIn(formik.touched, "custom_fields")[index].field_type &&
                  !_.isNil(getIn(formik.errors, "custom_fields")) &&
                  !_.isNil(getIn(formik.errors, "custom_fields")[index]) &&
                  getIn(formik.errors, "custom_fields")[index].field_type
                    ? getIn(formik.errors, "custom_fields")[index].field_type
                    : ""
                }
              />
              <Tooltip title="Remove custom field">
                <IconButton
                  onClick={() => onRemoveCustomFieldButtonClick({ index })}
                  sx={{ alignSelf: "center" }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
          <Button
            sx={{ width: "fit-content", alignSelf: "flex-end" }}
            onClick={onAddNewCustomFieldButtonClick}
          >
            + Add new field
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

export default MemoTemplateSignatureSectionForm;
